import { HttpErrorResponse } from "@angular/common/http";
import { computed, DestroyRef, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import {
    catchError,
    combineLatest,
    EMPTY,
    filter,
    first,
    map,
    Observable,
    of,
    throwError
} from "rxjs";
import {
    FHIRMiddlewareApiService,
    FormConfiguration,
    FormConfigurationSettings,
    ProjectResourceData,
    FormConfigurationElementSimpleControlType,
    AuthConfiguration,
    FormConfigurationDataset,
    FormConfigurationElement,
    FormConfigurationElementType,
    FormConfigurationElementControlBase,
    FormConfigurationElementParagraph,
    FormConfigurationOptionsType,
    FormConfigurationOptionsDefinitionDynamic
} from "@fhir-api";
import { tap } from "rxjs/operators";
import { AppStateService } from "./state/app-state.service";
import { AttributeDataTypeEnum, QualityRule, Scm } from "@logex/expression-validator";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { DataPath } from "./state/app-navigation-manager.service";
import { MrdmAuthService } from "@authorization";
import { buildOptionNameToTargetRefMap, mapRawCatalogRow } from "@app/util/external-optionset.util";
import { resolveRootEntity } from "@app/util/scm-root-entity.util";
import { SubjectInstance } from "./state/app-subject-manager.service";

export interface CatalogDefinition {
    project: string;
    optionDefinition: FormConfigurationOptionsDefinitionDynamic;
}

export const defaultSettings: FormConfigurationSettings = {
    defaultLanguage: "nl-NL",
    listPage: {
        columns: [
            {
                name: "upn"
            },
            {
                name: "naam"
            },
            {
                name: "gebdat"
            }
        ],
        defaultSortBy: "subjectLastEdited",
        defaultSortOrder: "descending"
    }
};
@Injectable({
    providedIn: "root"
})
export class AppProjectsService {
    private _appStateService = inject(AppStateService);
    private _authService = inject(MrdmAuthService);
    private _middlewareApiService = inject(FHIRMiddlewareApiService);
    private _router = inject(Router);
    private _destroyRef = inject(DestroyRef);

    projectsMap = computed(
        () =>
            new Map(
                this._appStateService
                    .projectList()
                    .map(entry => [
                        entry.organizationId,
                        [...entry.projects]
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .filter(
                                project =>
                                    project.properties.active && project.properties.selectable
                            )
                    ])
            )
    );

    private _selectedProject = signal<ProjectResourceData | null>(null);
    selectedProject = this._selectedProject.asReadonly();
    selectedProject$ = toObservable(this._selectedProject).pipe(
        filter(project => project !== null)
    );

    private _scm = signal<Scm | null>(null);
    scm = this._scm.asReadonly();
    private _formConfiguration = signal<FormConfiguration | null>(null);
    formConfiguration = this._formConfiguration.asReadonly();
    private _validationRules = signal<QualityRule[] | null>(null);
    validationRules = this._validationRules.asReadonly();
    private _authConfiguration = signal<AuthConfiguration | null>(null);
    authConfiguration = this._authConfiguration.asReadonly();
    private _ownerIds = signal<string[]>([]);
    ownerIds = this._ownerIds.asReadonly();

    ownerIdVariablePaths = computed(() => this.selectedProject()?.properties.ownerIdFilter ?? []);

    private _catalogCache = signal<Map<string, Record<string, string | number>[]>>(new Map());
    catalogCache = this._catalogCache.asReadonly();
    private _catalogDefinitions = signal<CatalogDefinition[]>([]);
    catalogDefinitions = this._catalogDefinitions.asReadonly();

    private _rootDataset = signal<string | null>(null);
    rootDataset = this._rootDataset.asReadonly();
    rootDataset$ = toObservable(this._rootDataset);

    private _rootSubject = signal<string | null>(null);
    rootSubject = this._rootSubject.asReadonly();

    rootPath = computed<DataPath | null>(() => {
        const rootSubject = this.rootSubject();
        const rootDataset = this.rootDataset();
        if (!rootDataset) {
            console.error("Root subject or root dataset is not set");
            return null;
        }
        return {
            subjectUri: rootSubject ?? "",
            datasetUri: rootDataset
        };
    });

    product = computed(
        () => this._selectedProject()?.properties.product?.replace("Product.", "") ?? null
    );
    productPermissions = computed(() => {
        const product = this.selectedProject()?.properties.product;
        return this._authService.getProductPermissions(
            product,
            this._appStateService.selectedOrganizationId()
        );
    });
    url = computed(() => `/registry/${this._selectedProject()?.uri}`);
    label = computed(
        () =>
            this._formConfiguration()?.label ??
            this._formConfiguration()?.name ??
            this._scm()?.modelName
    );
    description = computed(() => this._scm()?.description ?? "");
    defaultLanguage = computed(() => this._formConfiguration()?.settings?.defaultLanguage ?? "");
    defaultControl = computed(() => {
        return FormConfigurationElementSimpleControlType.Text;
    });

    settings = computed(() => {
        return {
            ...defaultSettings,
            ...(this.formConfiguration()?.settings ?? {})
        };
    });

    initialized = signal(false);
    initialized$ = toObservable(this.initialized);

    // --------------------------------------------------------------------------------------
    init(organizationUri: string, registryName: string): Observable<void> {
        this.initialized.set(false);
        this._appStateService.selectOrganizationAndRegistry(organizationUri, registryName);
        return this._selectProject$().pipe(
            takeUntilDestroyed(this._destroyRef),
            tap(() => this.initialized.set(true))
        );
    }

    getOrganizationProjects(organisationCode: number): ProjectResourceData[] | null {
        return this.projectsMap().get(organisationCode) ?? null;
    }

    resetSelectedProject(): void {
        this._selectedProject.set(null);
    }

    getProjectByUri(projectUri: string): ProjectResourceData | undefined {
        const selectedOrganizationId = this._appStateService.selectedOrganizationId();
        if (!selectedOrganizationId) return undefined;

        const projects = this.projectsMap().get(selectedOrganizationId);
        return projects?.find(project => project.uri === projectUri);
    }

    setRootSubject(subjectUri: string | null): void {
        this._rootSubject.set(subjectUri);
    }

    hasDifferentOwner(values: SubjectInstance): boolean {
        const currentOwnerId = this._appStateService.selectedOrganizationCicCode()?.toString();
        const ownerIdVariables = this.ownerIdVariablePaths();
        const datasetName = values.datasetUri.split(".").at(-1);

        return Object.entries(values.variable ?? {}).some(([variableName, variableValue]) => {
            const variableUri = `${datasetName}.${variableName}`;
            if (ownerIdVariables.includes(variableUri) && variableValue !== currentOwnerId) {
                return true;
            }

            return false;
        });
    }

    _selectProject$(): Observable<void> {
        const organization = this._appStateService.selectedOrganization();
        const selectedRegistryName = this._appStateService.selectedRegistryName();
        if (!organization || !selectedRegistryName) return EMPTY; // TODO WHAT SHOULD HAPPEN?

        return combineLatest([
            this._middlewareApiService
                .getFormConfiguration$(organization, selectedRegistryName)
                .pipe(
                    this._appStateService.catchRequestError$({
                        404: {
                            lcKey: "Form_configuration_not_found"
                        },
                        default: {
                            lcKey: "Load_form_configuration_error",
                            action: () => this._router.navigate(["/select"])
                        }
                    })
                ),
            this._middlewareApiService.getValidationRules$(organization, selectedRegistryName).pipe(
                this._appStateService.catchRequestError$({
                    404: { lcKey: "Validation_rules_not_found" },
                    default: {
                        lcKey: "Load_validation_rules_error",
                        action: () => this._router.navigate(["/select"])
                    }
                })
            ),
            this._middlewareApiService.getScm$(organization, selectedRegistryName).pipe(
                this._appStateService.catchRequestError$({
                    404: { lcKey: "Scm_not_found" },
                    default: {
                        lcKey: "Load_scm_error",
                        action: () => this._router.navigate(["/select"])
                    }
                })
            ),
            this._middlewareApiService
                .getAuthConfiguration$(organization, selectedRegistryName)
                .pipe(
                    this._appStateService.catchRequestError$({
                        404: { ignore: true },
                        default: {
                            lcKey: "Load_auth_configuration_error",
                            action: () => this._router.navigate(["/select"])
                        }
                    }),
                    catchError(err => {
                        if (err instanceof HttpErrorResponse) {
                            if (err.status === 404) {
                                return of(null);
                            }
                        }

                        return throwError(() => err);
                    })
                ),
            this._middlewareApiService.getOwnerIds$(organization, selectedRegistryName).pipe(
                this._appStateService.catchRequestError$({
                    404: { ignore: true },
                    default: {
                        lcKey: "Load_owner_ids_error",
                        action: () => this._router.navigate(["/select"])
                    }
                }),
                catchError(err => {
                    if (err instanceof HttpErrorResponse) {
                        if (err.status === 404) {
                            return of(null);
                        }
                    }

                    return throwError(() => err);
                })
            )
        ]).pipe(
            first(),
            map(([formConfiguration, validationRules, scm, authConfiguration, ownerIds]) => {
                if (formConfiguration.body && validationRules.body && scm.body) {
                    const project = this.getProjectByUri(selectedRegistryName);
                    this._authConfiguration.set(authConfiguration?.body ?? null);
                    this._ownerIds.set(ownerIds?.body?.ownerIdsWithErrors ?? []);

                    if (project) {
                        const normalizedScm = this._normalizeScmAttributeDataTypes(scm.body);
                        this._selectedProject.set(project);
                        this._formConfiguration.set(formConfiguration.body);
                        this._validationRules.set(validationRules.body);
                        this._scm.set(normalizedScm);
                        this._rootDataset.set(
                            this._getRootDatasetName(formConfiguration.body, normalizedScm)
                        );
                        this._preloadCatalogData(formConfiguration.body);
                    }
                }
            })
        );
    }

    getCachedCatalogData(
        project: string,
        datasetName: string
    ): Record<string, string | number>[] | null {
        const key = `${project}/${datasetName}`;
        const cache = this._catalogCache();
        return cache.has(key) ? cache.get(key)! : null;
    }

    private _preloadCatalogData(formConfiguration: FormConfiguration): void {
        const catalogDefs = this._extractCatalogDefinitions(formConfiguration);
        this._catalogDefinitions.set(catalogDefs);

        if (catalogDefs.length === 0) return;

        const organization = this._appStateService.selectedOrganization();
        const project = this._appStateService.selectedRegistryName();
        if (!organization || !project) return;

        const requests = catalogDefs.map(def => {
            const nameToTargetRef = buildOptionNameToTargetRefMap(
                def.optionDefinition.optionDefinition
            );
            return this._middlewareApiService
                .getExternalOptionset$(organization, project, def.project)
                .pipe(
                    map(response => {
                        const rawOptions = response.body?.options ?? [];
                        return {
                            key: def.project,
                            data: rawOptions.map(row => mapRawCatalogRow(row, nameToTargetRef))
                        };
                    }),
                    catchError(error => {
                        console.error(`Failed to preload catalog data for ${def.project}`, error);
                        return of({
                            key: def.project,
                            data: [] as Record<string, string | number>[]
                        });
                    })
                );
        });

        combineLatest(requests)
            .pipe(first(), takeUntilDestroyed(this._destroyRef))
            .subscribe(results => {
                const cache = new Map<string, Record<string, string | number>[]>();
                for (const { key, data } of results) {
                    cache.set(key, data);
                }
                this._catalogCache.set(cache);
            });
    }

    private _extractCatalogDefinitions(formConfiguration: FormConfiguration): CatalogDefinition[] {
        const catalogs: CatalogDefinition[] = [];

        for (const dataset of formConfiguration.datasets) {
            this._collectCatalogFromDataset(dataset, catalogs);
        }

        return catalogs;
    }

    private _collectCatalogFromDataset(
        dataset: FormConfigurationDataset,
        result: CatalogDefinition[]
    ): void {
        const allElements: FormConfigurationElement[] = [
            ...dataset.sections.flatMap(s => s.categories).flatMap(c => c.elements),
            ...(dataset.initializationSection?.categories.flatMap(c => c.elements) ?? [])
        ];

        for (const element of allElements) {
            const prefillDef = this._getPrefillOptionsDef(element);
            if (
                prefillDef &&
                prefillDef.type === FormConfigurationOptionsType.DynamicOptionSet &&
                prefillDef.optionsSource?.sourceDataset?.catalog &&
                prefillDef.optionsSource?.sourceDataset?.project
            ) {
                const project = prefillDef.optionsSource.sourceDataset.project;
                const alreadyAdded = result.some(c => c.project === project);
                if (!alreadyAdded) {
                    result.push({
                        project: project!,
                        optionDefinition: prefillDef
                    });
                }
            }
        }
    }

    private _getPrefillOptionsDef(
        element: FormConfigurationElement
    ): FormConfigurationOptionsDefinitionDynamic | null {
        if (element.type === FormConfigurationElementType.Control) {
            const control = element as FormConfigurationElementControlBase;
            if (control.prefillOptionsDef?.type === FormConfigurationOptionsType.DynamicOptionSet) {
                return control.prefillOptionsDef as FormConfigurationOptionsDefinitionDynamic;
            }
        }
        if (element.type === FormConfigurationElementType.Paragraph) {
            const paragraph = element as FormConfigurationElementParagraph;
            if (
                paragraph.prefillOptionsDef?.type === FormConfigurationOptionsType.DynamicOptionSet
            ) {
                return paragraph.prefillOptionsDef;
            }
        }
        return null;
    }

    /** Returns a cloned SCM with every entity attribute data type in lowercase. */
    private _normalizeScmAttributeDataTypes(scm: Scm): Scm {
        return {
            ...scm,
            childContracts: scm.childContracts.map(group => ({
                ...group,
                content: {
                    ...group.content,
                    childContracts: group.content.childContracts.map(entity => ({
                        ...entity,
                        content: {
                            ...entity.content,
                            attributes: entity.content.attributes.map(attribute => ({
                                ...attribute,
                                dataType: attribute.dataType.toLowerCase() as AttributeDataTypeEnum
                            }))
                        }
                    }))
                }
            }))
        };
    }

    private _getRootDatasetName(formConfiguration: FormConfiguration, scm: Scm): string {
        const rootDatasetName = resolveRootEntity(scm)?.instanceName;
        const isConfigured = formConfiguration.datasets.some(
            dataset => dataset.name === rootDatasetName
        );

        return isConfigured ? rootDatasetName! : formConfiguration.datasets[0].name;
    }
}
